import React, { useMemo, useRef, useState } from 'react';
import { CATEGORIES, Category, ISSUES } from '../data';
import { gsap, useGSAP, prefersReducedMotion } from '../../../lib/gsap';

/** Horizontal bars: number of documented issues per category. Single series, one hue. */
const CategoryBars = ({
   active,
   onToggle,
}: {
   active: Category | null;
   onToggle: (c: Category) => void;
}) => {
   const root = useRef<HTMLDivElement>(null);
   const [hover, setHover] = useState<Category | null>(null);

   const rows = useMemo(() => {
      const counts = new Map<Category, { first: number; between: number; second: number }>();
      ISSUES.forEach(i => {
         const c = counts.get(i.category) || { first: 0, between: 0, second: 0 };
         c[i.era] += 1;
         counts.set(i.category, c);
      });
      return (Object.keys(CATEGORIES) as Category[])
         .map(cat => {
            const c = counts.get(cat) || { first: 0, between: 0, second: 0 };
            return { cat, total: c.first + c.between + c.second, ...c };
         })
         .filter(r => r.total > 0)
         .sort((a, b) => b.total - a.total);
   }, []);
   const max = Math.max(...rows.map(r => r.total));

   useGSAP(
      () => {
         if (prefersReducedMotion()) return;
         gsap.from('[data-bar]', {
            scaleX: 0,
            transformOrigin: 'left',
            stagger: 0.07,
            duration: 1.4,
            ease: 'expo.out',
            scrollTrigger: { trigger: root.current, start: 'top 85%', once: true },
         });
      },
      { scope: root }
   );

   return (
      <div ref={root} role="group" aria-label="Filter by category" className="space-y-1">
         {rows.map(r => {
            const isActive = active === r.cat;
            const dimmed = active !== null && !isActive;
            return (
               <button
                  type="button"
                  key={r.cat}
                  onClick={() => onToggle(r.cat)}
                  onMouseEnter={() => setHover(r.cat)}
                  onMouseLeave={() => setHover(null)}
                  aria-pressed={isActive}
                  className={`group relative grid w-full grid-cols-[minmax(0,11rem)_1fr_2rem] items-center gap-4 rounded-sm px-2 py-2 text-left transition-opacity duration-300 hover:bg-white/[0.03] ${
                     dimmed ? 'opacity-35' : ''
                  }`}
               >
                  <span className="truncate text-sm text-bone-muted group-hover:text-bone">{CATEGORIES[r.cat]}</span>
                  <span className="relative h-3">
                     <span
                        data-bar
                        className="absolute inset-y-0 left-0 rounded-r-[4px] bg-signal"
                        style={{ width: `${(r.total / max) * 100}%` }}
                     />
                  </span>
                  <span className="text-right font-mono text-xs text-bone">{r.total}</span>

                  {hover === r.cat && (
                     <span
                        role="tooltip"
                        className="pointer-events-none absolute right-12 top-full z-10 mt-1 rounded-sm border border-white/10 bg-ink-900/95 px-3 py-2 font-mono text-[11px] text-bone-muted shadow-xl backdrop-blur-md"
                     >
                        First term <span className="text-bone">{r.first}</span> · Between{' '}
                        <span className="text-bone">{r.between}</span> · Second term{' '}
                        <span className="text-bone">{r.second}</span>
                     </span>
                  )}
               </button>
            );
         })}
      </div>
   );
};

export default CategoryBars;
